/**
 * Handbook service for TG information system
 * Handles handbook sections, content, and confirmations
 */

import { supabase } from '../lib/supabase'
import type {
  HandbookContent,
  HandbookSectionWithContent,
  HandbookConfirmation,
  SubcontractorConfirmationStatus
} from '../types/handbook'

// ============================================
// Handbook Sections
// ============================================

export async function getHandbookSections(): Promise<HandbookSectionWithContent[]> {
  const { data, error } = await supabase
    .from('handbook_sections')
    .select(`
      *,
      handbook_content(*)
    `)
    .eq('is_active', true)
    .order('sort_order')

  if (error) throw error

  return (data || []).map(s => {
    // Handle both array and object response from Supabase
    let content = null
    if (Array.isArray(s.handbook_content)) {
      content = s.handbook_content[0] || null
    } else if (s.handbook_content) {
      content = s.handbook_content
    }

    return {
      ...s,
      content,
      handbook_content: undefined
    }
  }) as HandbookSectionWithContent[]
}

export async function getHandbookSection(
  slug: string
): Promise<HandbookSectionWithContent | null> {
  const { data, error } = await supabase
    .from('handbook_sections')
    .select(`
      *,
      handbook_content(*)
    `)
    .eq('slug', slug)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw error
  }

  // Handle both array and object response from Supabase
  let content = null
  if (Array.isArray(data.handbook_content)) {
    content = data.handbook_content[0] || null
  } else if (data.handbook_content) {
    content = data.handbook_content
  }

  return {
    ...data,
    content,
    handbook_content: undefined
  } as HandbookSectionWithContent
}

// ============================================
// Handbook Section Management (Admin)
// ============================================

export interface CreateSectionData {
  slug: string
  title: string
  description?: string
  icon?: string
  color?: string
  sort_order?: number
}

export interface UpdateSectionData {
  title?: string
  description?: string
  icon?: string
  color?: string
  sort_order?: number
  is_active?: boolean
}

export async function createHandbookSection(data: CreateSectionData) {
  const { data: section, error } = await supabase
    .from('handbook_sections')
    .insert({
      slug: data.slug,
      title: data.title,
      description: data.description || null,
      icon: data.icon || 'Info',
      color: data.color || null,
      sort_order: data.sort_order || 0
    })
    .select()
    .single()

  if (error) throw error
  return section
}

export async function updateHandbookSection(id: string, data: UpdateSectionData) {
  const { data: section, error } = await supabase
    .from('handbook_sections')
    .update(data)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return section
}

export async function deleteHandbookSection(id: string) {
  const { error } = await supabase
    .from('handbook_sections')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// ============================================
// Handbook Content (Admin)
// ============================================

export async function updateHandbookContent(
  sectionId: string,
  content: string,
  updatedByName: string
): Promise<HandbookContent> {
  const { data: { user } } = await supabase.auth.getUser()

  // Check if content exists
  const { data: existing } = await supabase
    .from('handbook_content')
    .select('id')
    .eq('section_id', sectionId)
    .single()

  if (existing) {
    // Update existing content (no version bump - version is managed at handbook level)
    const { data, error } = await supabase
      .from('handbook_content')
      .update({
        content,
        updated_by_id: user?.id,
        updated_by_name: updatedByName
      })
      .eq('id', existing.id)
      .select()
      .single()

    if (error) throw error
    return data
  } else {
    // Create new content
    const { data, error } = await supabase
      .from('handbook_content')
      .insert({
        section_id: sectionId,
        content,
        updated_by_id: user?.id,
        updated_by_name: updatedByName
      })
      .select()
      .single()

    if (error) throw error
    return data
  }
}

// ============================================
// Handbook Version
// ============================================

export interface HandbookVersion {
  id: string
  version_number: number
  description: string
  published_by_id: string | null
  published_by_name: string | null
  published_at: string
  created_at: string
}

export async function getCurrentHandbookVersion(): Promise<number> {
  const { data, error } = await supabase
    .from('handbook_versions')
    .select('version_number')
    .order('version_number', { ascending: false })
    .limit(1)

  if (error) throw error
  return data?.[0]?.version_number || 0
}

export async function getHandbookVersionHistory(): Promise<HandbookVersion[]> {
  const { data, error } = await supabase
    .from('handbook_versions')
    .select('*')
    .order('version_number', { ascending: false })

  if (error) throw error
  return data || []
}

export async function getLatestHandbookVersion(): Promise<HandbookVersion | null> {
  const { data, error } = await supabase
    .from('handbook_versions')
    .select('*')
    .order('version_number', { ascending: false })
    .limit(1)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw error
  }
  return data
}

export async function publishNewHandbookVersion(
  description: string,
  publishedByName: string
): Promise<HandbookVersion> {
  const { data: { user } } = await supabase.auth.getUser()

  // Get current max version
  const currentVersion = await getCurrentHandbookVersion()
  const newVersion = currentVersion + 1

  const { data, error } = await supabase
    .from('handbook_versions')
    .insert({
      version_number: newVersion,
      description,
      published_by_id: user?.id,
      published_by_name: publishedByName
    })
    .select()
    .single()

  if (error) throw error
  return data
}

// ============================================
// Handbook Confirmations
// ============================================

export async function getSubcontractorConfirmation(
  subcontractorId: string
): Promise<HandbookConfirmation | null> {
  const { data, error } = await supabase
    .from('handbook_confirmations')
    .select('*')
    .eq('subcontractor_id', subcontractorId)
    .order('confirmed_version', { ascending: false })
    .limit(1)

  if (error) throw error
  return data?.[0] || null
}

export async function confirmHandbook(
  subcontractorId: string,
  version: number
): Promise<HandbookConfirmation> {
  const { data: { user } } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('handbook_confirmations')
    .insert({
      subcontractor_id: subcontractorId,
      user_id: user?.id,
      confirmed_version: version
    })
    .select()
    .single()

  if (error) throw error
  return data
}

export async function hasConfirmedCurrentVersion(
  subcontractorId: string
): Promise<boolean> {
  const [confirmation, currentVersion] = await Promise.all([
    getSubcontractorConfirmation(subcontractorId),
    getCurrentHandbookVersion()
  ])

  // If there's no content yet (version 0), consider it confirmed
  if (currentVersion === 0) return true

  return confirmation?.confirmed_version === currentVersion
}

export async function needsHandbookConfirmation(
  subcontractorId: string
): Promise<{ needsConfirmation: boolean; currentVersion: number; confirmedVersion: number | null }> {
  const [confirmation, currentVersion] = await Promise.all([
    getSubcontractorConfirmation(subcontractorId),
    getCurrentHandbookVersion()
  ])

  // If there's no content yet (version 0), no confirmation needed
  if (currentVersion === 0) {
    return { needsConfirmation: false, currentVersion: 0, confirmedVersion: null }
  }

  const confirmedVersion = confirmation?.confirmed_version || null
  const needsConfirmation = confirmedVersion !== currentVersion

  return { needsConfirmation, currentVersion, confirmedVersion }
}

// ============================================
// Confirmation Statistics (Admin)
// ============================================

export async function getConfirmationStatistics(): Promise<SubcontractorConfirmationStatus[]> {
  const currentVersion = await getCurrentHandbookVersion()

  // Get all non-admin subcontractors
  const { data: subcontractors, error: subError } = await supabase
    .from('subcontractors')
    .select('id, name')
    .eq('is_active', true)
    .eq('is_admin', false)
    .order('name')

  if (subError) throw subError

  // Get all confirmations
  const { data: confirmations, error: confError } = await supabase
    .from('handbook_confirmations')
    .select('subcontractor_id, confirmed_version, confirmed_at')
    .order('confirmed_version', { ascending: false })

  if (confError) throw confError

  // Build map of latest confirmation per subcontractor
  const confirmationMap = new Map<string, { version: number; confirmed_at: string }>()
  for (const conf of confirmations || []) {
    if (!confirmationMap.has(conf.subcontractor_id)) {
      confirmationMap.set(conf.subcontractor_id, {
        version: conf.confirmed_version,
        confirmed_at: conf.confirmed_at
      })
    }
  }

  return (subcontractors || []).map(s => {
    const conf = confirmationMap.get(s.id)
    return {
      subcontractor_id: s.id,
      subcontractor_name: s.name,
      has_confirmed: conf !== undefined,
      confirmed_version: conf?.version || null,
      confirmed_at: conf?.confirmed_at || null,
      current_version: currentVersion,
      needs_reconfirmation: conf ? conf.version < currentVersion : true
    }
  })
}
